import { gql, type TypedDocumentNode } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Orb } from '@sorare/core/src/components/Orb';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { ensureHexColor } from '@sorare/core/src/lib/colors.ts';
import { isType } from '@sorare/core/src/lib/gql';

import { isClaimableNow } from '#src/components/series/isClaimableNow.ts';

import type {
  DailyRewardsCalendarOrb_cardSet,
  DailyRewardsCalendarOrb_taskInterface,
} from './__generated__/index.graphql.ts';

type Props = {
  calendarTasksTrack: DailyRewardsCalendarOrb_taskInterface;
  cardSet?: Nullable<DailyRewardsCalendarOrb_cardSet>;
  refetch: () => Promise<unknown>;
  onOpen: () => void;
};

export const DailyRewardsCalendarOrb = ({
  calendarTasksTrack,
  cardSet,
  refetch,
  onOpen,
}: Props) => {
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  const orbColor = ensureHexColor(cardSet?.branding?.colors?.main);

  if (!isType(calendarTasksTrack, 'TasksTrack')) {
    return null;
  }

  const { steps, currentStep } = calendarTasksTrack;
  const claimableCount = steps.filter(isClaimableNow).length;
  const everyRewardClaimed = steps.every(
    step => step.aasmState === ManagerProgressionTaskState.CLAIMED
  );

  if (steps.length === 0 || everyRewardClaimed) {
    return null;
  }

  const isClaimable = claimableCount > 0;
  const nextReward = currentStep?.rewardConfigs.at(0);
  const nextRewardClaimableAt = currentStep?.claimableAt ?? dailyTasksExpiresAt;

  return (
    <Orb
      showNotificationDot={isClaimable}
      onClick={onOpen}
      color={orbColor}
      glow={isClaimable}
      chipState={isClaimable ? 'reward' : 'timeLeft'}
      label={
        isClaimable || !nextRewardClaimableAt ? null : (
          <TimeLeft time={nextRewardClaimableAt} onEnded={refetch} />
        )
      }
    >
      {!!nextReward && (
        <RewardConfigImage anyRewardConfig={nextReward} size={3} />
      )}
    </Orb>
  );
};

DailyRewardsCalendarOrb.fragments = {
  cardSet: gql`
    fragment DailyRewardsCalendarOrb_cardSet on CardSet {
      slug
      branding {
        id
        colors {
          main
        }
      }
    }
  ` as TypedDocumentNode<DailyRewardsCalendarOrb_cardSet>,
  taskInterface: gql`
    fragment DailyRewardsCalendarOrb_taskInterface on TaskInterface {
      id
      ... on TasksTrack {
        id
        steps {
          id
          aasmState
          claimableAt
        }
        currentStep {
          id
          claimableAt
          rewardConfigs {
            id
            ...RewardConfigImage_anyRewardConfigInterface
          }
        }
      }
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<DailyRewardsCalendarOrb_taskInterface>,
};
